import {
	IRegEdMetadataFormImportResult,
	useRegEdBrokerDealerFormatsQuery,
	useRegEdBrokerDealerSchemaQuery,
	useRegEdBrokerDealersQuery,
	useRegEdMetadataSchemaImportMutation,
} from '@Queries/RegEdCompliance';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { RegEdMetadataForm } from '../../../../web/components/compliance/RegEdMetadataForm';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Field, FieldLabel } from '../../../../web/components-v2/Field';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { styleSheet } from './styles';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const BROKER_DEALER_FIELD_ID = 'reged-metadata-broker-dealer';
const PREVIEW_FORMAT_FIELD_ID = 'reged-metadata-preview-format';

const count = (n: number | undefined, noun: string) => `${n ?? 0} ${n === 1 ? noun : `${noun}s`}`;

/**
 * Developer tool for importing a broker-dealer's RegEd metadata schema (AdTrax xlsx). The write is
 * broker-dealer-scoped and cross-tenant — it runs off the account/impersonation surface entirely.
 */
function _RegEdMetadataSchema() {
	const fullscreenModal = useFullscreenModal();
	const { data: brokerDealers } = useRegEdBrokerDealersQuery();

	const [selectedBrokerDealerId, setSelectedBrokerDealerId] = React.useState('');
	// Tagged with the firm it was for: a cross-tenant replace's counts are unreadable — worse,
	// misleading — if they can outlive a switch to a different broker-dealer.
	const [importResult, setImportResult] = React.useState<{
		brokerDealerId: string;
		result: IRegEdMetadataFormImportResult;
	} | null>(null);
	const [isDragging, setIsDragging] = React.useState(false);
	const [pendingFile, setPendingFile] = React.useState<File | null>(null);
	const [previewFormat, setPreviewFormat] = React.useState('');
	const [previewAnswers, setPreviewAnswers] = React.useState<Record<string, unknown>>({});
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const importMutation = useRegEdMetadataSchemaImportMutation({
		onError: err => toast.error(err?.systemMessage || 'Metadata import failed.'),
		onSuccess: (result, { brokerDealerId }) => setImportResult({ brokerDealerId, result }),
	});

	const selectedBrokerDealer = (brokerDealers ?? []).find(bd => bd.id === selectedBrokerDealerId);
	const shownResult = importResult?.brokerDealerId === selectedBrokerDealerId ? importResult.result : null;
	const formsFailed = shownResult?.formsFailed ?? 0;

	const { data: formats } = useRegEdBrokerDealerFormatsQuery(selectedBrokerDealerId || null);
	const { data: previewSchema, isFetching: isPreviewFetching } = useRegEdBrokerDealerSchemaQuery(
		selectedBrokerDealerId || null,
		previewFormat || null
	);

	const handleFile = (file: File | undefined) => {
		if (!file || importMutation.isPending) {
			return;
		}
		if (file.type !== XLSX_MIME && !file.name.toLowerCase().endsWith('.xlsx')) {
			toast.error('Please upload an Excel (.xlsx) file.');
			return;
		}
		if (!selectedBrokerDealerId) {
			toast.error('Select a broker-dealer before importing a schema.');
			return;
		}
		setPendingFile(file);
	};

	const confirmImport = () => {
		if (!pendingFile || !selectedBrokerDealerId) {
			return;
		}
		setImportResult(null);
		importMutation.mutate({ brokerDealerId: selectedBrokerDealerId, file: pendingFile });
		setPendingFile(null);
	};

	return (
		<>
			<MultiContainerHeader fullscreenHeader='RegEd Metadata Schema' />
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.intro)}>Import an AdTrax metadata export (.xlsx) for one broker-dealer.</div>

				<div className={css(styleSheet.panel, styleSheet.stepEnter)}>
					<Field>
						<FieldLabel htmlFor={BROKER_DEALER_FIELD_ID} styles={[styleSheet.eyebrow]}>
							Step 1 of 3 — Broker-dealer
						</FieldLabel>
						<Select
							value={selectedBrokerDealerId}
							onValueChange={id => {
								setSelectedBrokerDealerId(id);
								setImportResult(null);
								setPreviewFormat('');
								setPreviewAnswers({});
							}}
						>
							<SelectTrigger id={BROKER_DEALER_FIELD_ID} styles={styleSheet.selectTrigger}>
								<span>{selectedBrokerDealer?.name ?? 'Select a broker-dealer…'}</span>
							</SelectTrigger>
							<SelectContent>
								{(brokerDealers ?? [])
									.filter(bd => !!bd.id)
									.map(bd => (
										<SelectItem key={bd.id} value={bd.id!}>
											{bd.name ?? bd.id}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
					</Field>

					{selectedBrokerDealerId ? (
						<div className={css(styleSheet.scopeLine)}>
							An import replaces every format's stored schema for{' '}
							<span className={css(styleSheet.targetName)}>{selectedBrokerDealer?.name ?? selectedBrokerDealerId}</span>
							, on every account that belongs to it.
						</div>
					) : null}
				</div>

				{/* The import is broker-dealer-scoped, so a drop target with nothing to scope it to can only
				    fail — offering it invites the mistake. */}
				{selectedBrokerDealerId ? (
					<div className={css(styleSheet.panel, styleSheet.stepEnter)}>
						<span className={css(styleSheet.eyebrow)}>Step 2 of 3 — Import</span>
						<div
							className={css(styleSheet.dropzone, isDragging && styleSheet.dropzoneActive)}
							role='button'
							tabIndex={0}
							onClick={() => {
								if (!importMutation.isPending) {
									fileInputRef.current?.click();
								}
							}}
							onKeyDown={e => {
								if ((e.key === 'Enter' || e.key === ' ') && !importMutation.isPending) {
									e.preventDefault();
									fileInputRef.current?.click();
								}
							}}
							onDragOver={e => {
								e.preventDefault();
								setIsDragging(true);
							}}
							onDragLeave={() => setIsDragging(false)}
							onDrop={e => {
								e.preventDefault();
								setIsDragging(false);
								handleFile(e.dataTransfer.files?.[0]);
							}}
						>
							<span>{importMutation.isPending ? 'Importing…' : 'Drop the .xlsx here, or click to choose a file'}</span>
							<input
								ref={fileInputRef}
								className={css(styleSheet.hiddenInput)}
								type='file'
								accept={`.xlsx,${XLSX_MIME}`}
								onChange={e => {
									handleFile(e.target.files?.[0]);
									e.target.value = '';
								}}
							/>
						</div>
					</div>
				) : null}

				{shownResult ? (
					<div
						className={css(
							styleSheet.resultPanel,
							styleSheet.stepEnter,
							formsFailed > 0 && styleSheet.resultPanelPartial
						)}
					>
						<span className={css(styleSheet.eyebrow)}>Result</span>
						<div className={css(styleSheet.counts)}>
							{count(shownResult.formsWritten, 'form')} · {count(shownResult.questionsImported, 'question')} ·{' '}
							{count(shownResult.optionsImported, 'option')}
						</div>
						{formsFailed > 0 ? (
							<div className={css(styleSheet.failedCount)}>{count(formsFailed, 'form')} failed to import</div>
						) : null}
						{shownResult.warnings && shownResult.warnings.length > 0 ? (
							<>
								<div className={css(styleSheet.warningsTitle)}>Warnings ({shownResult.warnings.length})</div>
								<ul className={css(styleSheet.warningsList)}>
									{shownResult.warnings.map((w, i) => (
										<li key={i}>{w}</li>
									))}
								</ul>
							</>
						) : null}
					</div>
				) : null}

				{shownResult ? (
					<div className={css(styleSheet.panel, styleSheet.stepEnter)}>
						<Field>
							<FieldLabel htmlFor={PREVIEW_FORMAT_FIELD_ID} styles={[styleSheet.eyebrow]}>
								Step 3 of 3 — Preview a format
							</FieldLabel>
							<Select
								value={previewFormat}
								onValueChange={format => {
									setPreviewFormat(format);
									setPreviewAnswers({});
								}}
							>
								<SelectTrigger id={PREVIEW_FORMAT_FIELD_ID} styles={styleSheet.selectTrigger}>
									<span>{previewFormat || 'Select a format…'}</span>
								</SelectTrigger>
								<SelectContent>
									{(formats ?? []).map(f => (
										<SelectItem key={f} value={f}>
											{f}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>

						{previewFormat && !isPreviewFetching ? (
							previewSchema ? (
								<>
									<span className={css(styleSheet.gateHint)}>
										Answer fields to reveal the conditional questions they trigger. Nothing here is saved.
									</span>
									<div className={css(styleSheet.previewFrame)}>
										<RegEdMetadataForm answers={previewAnswers} onChange={setPreviewAnswers} schema={previewSchema} />
									</div>
								</>
							) : (
								// The firm's catalog lists the format, so an empty read means nothing has been imported
								// for it — not that the firm is unknown.
								<span className={css(styleSheet.gateHint)}>No schema imported for {previewFormat} yet.</span>
							)
						) : null}
					</div>
				) : null}

				{/* Before an import there is nothing to finish — the header's close is the only exit. */}
				{shownResult ? (
					<div className={css(styleSheet.footer)}>
						<Button variant='secondary' onClick={fullscreenModal.dismissModal} styles={[styleSheet.closeButton]}>
							Close
						</Button>
					</div>
				) : null}
			</div>

			<Dialog open={!!pendingFile} onOpenChange={open => (open ? null : setPendingFile(null))}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle styles={[styleSheet.dialogTitle]}>
							Replace schema for {selectedBrokerDealer?.name ?? selectedBrokerDealerId}?
						</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.confirmBody)}>
							<div>
								<strong>{pendingFile?.name}</strong> replaces the stored schema for every format on this broker-dealer.
							</div>
							<div>Every account that belongs to it picks up the new schema. This cannot be undone.</div>
						</div>
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' onClick={() => setPendingFile(null)}>
							Cancel
						</Button>
						<Button variant='destructive' onClick={confirmImport}>
							Replace schema
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</>
	);
}

export const RegEdMetadataSchema = _RegEdMetadataSchema;
